/**
 * Catalog route tree, composable under any parent path.
 *
 * Shell mounts it at `/catalog/*`; standalone mounts at the repo root. The
 * `catalogRoutes(basePath)` factory prefixes sidebar `<Link>`s with the
 * mount path. Route-object paths stay relative so React Router resolves
 * them against the mount point in either mode.
 */

import { lazy, Suspense, createContext, useContext } from 'react';
import { Outlet, type RouteObject } from 'react-router-dom';
import { Spinner } from '@fluentui/react-components';
import { AppShell, type NavItem } from '@widgets/app-shell';
import { CatalogActivityLog } from '@catalog/features/activity-log/CatalogActivityLog';
import { ErrorBoundary } from '@shared/ui';
import { ProtectedRoute } from '@shared/auth';
import {
  BoardRegular,
  BoxMultipleRegular,
  ClipboardTaskListLtrRegular,
  WrenchScrewdriverRegular,
  ListRegular,
  LinkRegular,
  EyeRegular,
  SettingsRegular,
  MailRegular,
} from '@fluentui/react-icons';

const SolutionsPage = lazy(() => import('@catalog/pages/solutions').then(m => ({ default: m.SolutionsPage })));
const ManagedListsPage = lazy(() => import('@catalog/pages/managed-lists').then(m => ({ default: m.ManagedListsPage })));
const GroupInfoPage = lazy(() => import('@catalog/pages/group-info').then(m => ({ default: m.GroupInfoPage })));
const MasterDataPage = lazy(() => import('@catalog/pages/master-data').then(m => ({ default: m.MasterDataPage })));
const PartnerIdsPage = lazy(() => import('@catalog/pages/partner-ids').then(m => ({ default: m.PartnerIdsPage })));
const AdminConfigPage = lazy(() => import('@catalog/pages/admin-config').then(m => ({ default: m.AdminConfigPage })));
const DashboardPage = lazy(() => import('@catalog/pages/dashboard').then(m => ({ default: m.DashboardPage })));
const SolutionOverviewPage = lazy(() => import('@catalog/pages/solution-overview').then(m => ({ default: m.SolutionOverviewPage })));
const CatalogReportPage = lazy(() => import('@catalog/pages/catalog-report').then(m => ({ default: m.CatalogReportPage })));
const DemoPreviewPage = lazy(() => import('@catalog/pages/demo-preview').then(m => ({ default: m.DemoPreviewPage })));
const ImplementationPage = lazy(() => import('@catalog/pages/implementation').then(m => ({ default: m.ImplementationPage })));
const TemplateEditorPage = lazy(() => import('@catalog/pages/implementation').then(m => ({ default: m.TemplateEditorPage })));
const SectionEditorPage = lazy(() => import('@catalog/pages/implementation').then(m => ({ default: m.SectionEditorPage })));
const ImpGuidePage = lazy(() => import('@catalog/pages/implementation').then(m => ({ default: m.ImpGuidePage })));
const EmailTemplatesPage = lazy(() => import('@catalog/pages/email-templates').then(m => ({ default: m.EmailTemplatesPage })));
const ServiceEditorPage = lazy(() => import('@catalog/pages/service-editor').then(m => ({ default: m.ServiceEditorPage })));

function LazyPage({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<Spinner style={{ padding: '2rem' }} label="Loading..." />}>{children}</Suspense>;
}

const CatalogBasePathContext = createContext<string>('');

function buildNavItems(basePath: string): NavItem[] {
  const p = (suffix: string) => `${basePath}${suffix}` || '/';
  return [
    { path: p('/'), label: 'Dashboard', icon: BoardRegular },
    { path: p('/solutions'), label: 'Solutions', icon: BoxMultipleRegular },
    { path: p('/group-info'), label: 'Group Info Template', icon: ClipboardTaskListLtrRegular },
    { path: p('/implementation'), label: 'Implementation', icon: WrenchScrewdriverRegular },
    { path: p('/managed-lists'), label: 'Managed Lists', icon: ListRegular },
    { path: p('/partner-ids'), label: 'Partner IDs', icon: LinkRegular },
    { path: p('/email-templates'), label: 'Email Templates', icon: MailRegular },
    { path: p('/demo-preview'), label: 'Demo Preview', icon: EyeRegular },
    { path: p('/admin-config'), label: 'Configuration', icon: SettingsRegular },
  ];
}

export function CatalogRootLayout() {
  const basePath = useContext(CatalogBasePathContext);
  const navItems = buildNavItems(basePath);
  return (
    <ProtectedRoute appRolePrefix="Catalog">
      <ErrorBoundary>
        <AppShell
          appId="catalog"
          headerColor="#3F4C5C"
          navItems={navItems}
          settingsContent={<CatalogActivityLog />}
        >
          <Outlet />
        </AppShell>
      </ErrorBoundary>
    </ProtectedRoute>
  );
}

/**
 * Returns the Catalog route tree, with sidebar links prefixed for the mount
 * path. Standalone: `catalogRoutes()` at repo root. Shell: `catalogRoutes('/catalog')`.
 */
export function catalogRoutes(basePath: string = ''): RouteObject[] {
  return [
    {
      element: (
        <CatalogBasePathContext.Provider value={basePath}>
          <CatalogRootLayout />
        </CatalogBasePathContext.Provider>
      ),
      children: [
        { index: true, element: <LazyPage><DashboardPage /></LazyPage> },
        { path: 'solutions', element: <LazyPage><SolutionsPage /></LazyPage> },
        { path: 'solutions/:solutionId/overview', element: <LazyPage><SolutionOverviewPage /></LazyPage> },
        { path: 'solutions/report', element: <LazyPage><CatalogReportPage /></LazyPage> },
        { path: 'services/:serviceId', element: <LazyPage><ServiceEditorPage /></LazyPage> },
        { path: 'managed-lists', element: <LazyPage><ManagedListsPage /></LazyPage> },
        { path: 'group-info', element: <LazyPage><GroupInfoPage /></LazyPage> },
        { path: 'master-data', element: <LazyPage><MasterDataPage /></LazyPage> },
        { path: 'partner-ids', element: <LazyPage><PartnerIdsPage /></LazyPage> },
        { path: 'implementation', element: <LazyPage><ImplementationPage /></LazyPage> },
        { path: 'implementation/guide', element: <LazyPage><ImpGuidePage /></LazyPage> },
        { path: 'implementation/section/:sectionId', element: <LazyPage><SectionEditorPage /></LazyPage> },
        { path: 'implementation/:templateId', element: <LazyPage><TemplateEditorPage /></LazyPage> },
        { path: 'demo-preview', element: <LazyPage><DemoPreviewPage /></LazyPage> },
        { path: 'admin-config', element: <LazyPage><AdminConfigPage /></LazyPage> },
        { path: 'email-templates', element: <LazyPage><EmailTemplatesPage /></LazyPage> },
      ],
    },
  ];
}

